/**
 * Adapter: real API UserEntity → PanelUser / ProfileUser view-models.
 *
 * Only maps fields that exist in the real API object; absent data is left
 * undefined so the UI degrades gracefully (guards already exist in the design).
 */

import {
  UserEntity,
  AccountStatus,
  MembershipType,
  OnboardingStep,
  UserType,
  ProfileGender,
  INTERVIEW_TOPIC,
} from 'src/store/userApi';

import type { PanelUser, ProfileUser, ProfileFields } from './types';
import type { CarouselPhoto } from 'src/ui/Carousel';

// ---------- helpers ----------

function genderString(g?: ProfileGender): string | undefined {
  if (g === ProfileGender.FEMALE) return 'female';
  if (g === ProfileGender.MALE) return 'male';
  if (g === ProfileGender.NON_BINARY) return 'nonbinary';
  return undefined;
}

function userTypeString(t?: UserType): string | undefined {
  if (t === UserType.SAMPLE) return 'sample';
  if (t === UserType.TEST) return 'test';
  if (t === UserType.NORMAL) return 'normal';
  return undefined;
}

function onboardingStepKey(s?: OnboardingStep): string | undefined {
  const MAP: Record<OnboardingStep, string> = {
    [OnboardingStep.START]: 'start',
    [OnboardingStep.READY_FOR_INTERVIEW]: 'ready_interview',
    [OnboardingStep.INTERVIEW]: 'interview',
    [OnboardingStep.UPLOAD_PHOTO]: 'upload_photo',
    [OnboardingStep.PLAN_COMPARISON]: 'plan_comparison',
    [OnboardingStep.SUBSCRIPTION]: 'subscription',
    [OnboardingStep.REVIEW_PROFILE]: 'review_profile',
    [OnboardingStep.COMPLETED]: 'completed',
  };
  return s !== undefined ? MAP[s] : undefined;
}

function accountStatusKey(s?: AccountStatus): string | undefined {
  if (s === AccountStatus.ACTIVE) return 'active';
  if (s === AccountStatus.BLOCKED) return 'blocked';
  if (s === AccountStatus.PAUSED) return 'paused';
  if (s === AccountStatus.SKIP_MATCH) return 'skip_match';
  return undefined;
}

function membershipKey(m?: MembershipType): string | undefined {
  if (m === MembershipType.FREEMIUM) return 'freemium';
  if (m === MembershipType.PREMIUM_TRIAL) return 'premium_trial';
  if (m === MembershipType.PREMIUM) return 'premium';
  return undefined;
}

function interviewTopicKey(t?: INTERVIEW_TOPIC | null): string | undefined {
  if (!t) return undefined;
  // Map INTERVIEW_TOPIC enum values → design string keys
  const MAP: Record<string, string> = {
    [INTERVIEW_TOPIC.INIT]: 'init',
    [INTERVIEW_TOPIC.LIFE_STYLE]: 'lifestyle',
    [INTERVIEW_TOPIC.FAMILY]: 'family',
    [INTERVIEW_TOPIC.RELATIONSHIP]: 'relationship',
  };
  return MAP[t] ?? undefined;
}

function locationString(profile: any): string | undefined {
  const parts: string[] = [];
  if (profile?.city) parts.push(profile.city);
  if (profile?.state) parts.push(profile.state);
  if (profile?.country) parts.push(profile.country);
  return parts.length > 0 ? parts.join(', ') : undefined;
}

function photosToCarousel(
  photos?: UserEntity['photos']
): Array<string | CarouselPhoto> {
  if (!photos || photos.length === 0) return [];
  return photos.map((p) => {
    const url = p.lImage?.url || p.mImage?.url || p.sImage?.url;
    if (url) return url;
    // fallback — no URL available, show placeholder
    return { bg: 'var(--bg-muted)', emoji: '📷' } as CarouselPhoto;
  });
}

// First available photo URL for the list thumbnail. Prefers the small image
// (loaded by the admin list endpoint); falls back to medium/large if present.
function firstPhotoUrl(photos?: UserEntity['photos']): string | undefined {
  if (!photos || photos.length === 0) return undefined;
  for (const p of photos) {
    const url = p.sImage?.url || p.mImage?.url || p.lImage?.url;
    if (url) return url;
  }
  return undefined;
}

// Extract answers from profile.answers (the interview answer bag)
function extractAnswers(profile: any): Record<string, any> {
  return profile?.answers ?? {};
}

// Attempt to read a string answer from the answers bag, supporting
// either a string value or an object with .en / .cn fields.
function answerString(
  answers: Record<string, any>,
  key: string
): string | undefined {
  const v = answers[key];
  if (!v && v !== 0) return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && (v.en || v.cn)) return v.en || v.cn;
  if (typeof v === 'number') return String(v);
  return undefined;
}

// ---------- public API ----------

/**
 * Convert a real API UserEntity to the PanelUser shape for UserSearchPanel / UserRow.
 */
export function toPanelUser(u: UserEntity): PanelUser {
  const p = u.profile ?? {};

  return {
    id: u.id,
    name: p.name || u.email,
    email: u.email,
    gender: genderString(p.gender),
    hue: p.hue,
    image: firstPhotoUrl(u.photos),
    age: p.age,
    loc: locationString(p),
    online: p.online ?? false,
    deleted: !!u.deletedAt,
    deletedAt: u.deletedAt
      ? new Date(u.deletedAt).toISOString().slice(0, 10)
      : undefined,
    type: userTypeString(u.userType),
    step: onboardingStepKey(u.onboardingStep),
    topic: interviewTopicKey(u.interviewProgress?.currentTopic),
    status: accountStatusKey(u.accountStatus),
    plan: membershipKey(u.membershipType),
  };
}

/**
 * Convert a real API UserEntity to the ProfileUser shape for ProfileDisplay.
 */
export function toProfileUser(u: UserEntity): ProfileUser {
  const p = u.profile ?? {};
  const answers = extractAnswers(p);

  const fields: ProfileFields = {
    // The real profile stores the summary/bio in p.summary (not p.bio).
    // Answers bag may also carry a 'bio' key from early interview versions.
    bio: answerString(answers, 'bio') ?? p.summary ?? p.bio,
    occupation: answerString(answers, 'occupation') ?? p.occupation,
    height: answerString(answers, 'height') ?? p.height,
    education: answerString(answers, 'education') ?? p.education,
    languages: answerString(answers, 'languages') ?? p.languages,
    dietary: answerString(answers, 'dietary') ?? p.dietary,
    smoking: answerString(answers, 'smoking') ?? p.smoking,
    looking: answerString(answers, 'looking') ?? p.looking,
    interests: Array.isArray(answers.interests)
      ? answers.interests
      : Array.isArray(p.interests)
      ? p.interests
      : undefined,
  };

  return {
    id: u.id,
    name: p.name || u.email,
    gender: genderString(p.gender),
    age: p.age,
    loc: locationString(p),
    online: p.online ?? false,
    photos: photosToCarousel(u.photos),
    fields,
  };
}
